import { WorkHistoryOutlined } from "@mui/icons-material";
import { Card, CircularProgress } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesPercent } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesPercent";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../../components/inputs/SearchInput";
import Flx from "../../../components/layout/Flx";
import SidebarAddExperienceButton from "../../../components/sidebars/DashboardSidebar/buttons/SidebarAddExperienceButton";
import { DASHBOARD_SIDEBAR_TYPES } from "../../../components/sidebars/DashboardSidebar/DashboardSidebar";
import Txt from "../../../components/typography/Txt";
import EmptyStateCard from "../../../components/ui/EmptyStateCard";
import { sidebarOpen } from "../../../redux/reducers/sidebarReducer";

import ProfileSection from "../components/ProfileSection";

const BorrowerExperienceSection = ({ activeTab, keyName }) => {
  const profile = useSelector((state) => state?.profile);

  const validatedExperience = profile?.validatedExperience || [];

  return (
    <ProfileSection
      activeTab={activeTab}
      title={"Experience"}
      icon={<WorkHistoryOutlined sx={{ mr: 0.3 }} className="thin" />}
      endContent={
        isEmpty(validatedExperience) ? null : <SidebarAddExperienceButton />
      }
    >
      <Flx column g={2}>
        <Txt secondary>
          Please enter details about past investments covering fix and flip,
          ground up construction, and rental property. Include details about the
          purchase date, purchase price, any renovation budgets. For projects
          that have been resolved, please include whether you sold, refinanced
          and/or holding long term. For projects you have on the market, please
          include those details as well. Include the name of LLCs or other
          entities that you closed under in each experience record.{" "}
          <i>
            Please do not include any primary residence or personal properties.
          </i>
        </Txt>
        <Txt secondary>
          Provide documentation of Track Record such as an itemized transaction
          history of with past flips including purchase and sale dates and
          dollar amounts. Do not provide details on past primary residences,
          only focus on investment properties. If those past transactions were
          through different entities, you may be asked for proof of entity
          ownership or purchase and/or sale closing documents.
        </Txt>
      </Flx>
      <ContentRenderer activeTab={activeTab} />
    </ProfileSection>
  );
};

const ContentRenderer = () => {
  const profile = useSelector((state) => state?.profile);
  const dispatch = useDispatch();
  const validatedExperience = profile?.validatedExperience || [];

  if (isNil(profile)) {
    return (
      <Card sx={{ py: 3 }}>
        <Flx g={2} center fw column>
          <CircularProgress size={18} />
          <Txt secondary>Getting your profile information...</Txt>
        </Flx>
      </Card>
    );
  }

  if (isEmpty(validatedExperience)) {
    return (
      <EmptyStateCard
        icon={<WorkHistoryOutlined />}
        title={"No Experience Records Yet"}
        description={
          "Add your real estate investment history to help us better understand your experience and qualifications."
        }
        buttonText={"Add First Record"}
        onClick={() =>
          dispatch(sidebarOpen(DASHBOARD_SIDEBAR_TYPES.ADD_EXPERIENCE))
        }
      />
    );
  }

  if (!isEmpty(validatedExperience)) {
    return (
      <ExperienceTable
        // onRowClick={(data) =>
        //   dispatch(sidebarOpen(DASHBOARD_SIDEBAR_TYPES.EDIT_EXPERIENCE, data))
        // }
        experiences={validatedExperience}
      />
    );
  }
  return;
};

const ExperienceTable = ({
  experiences = [],
  onAdd,
  onEdit,
  onDelete,
  onRowClick,
}) => {
  const ref = useRef(null);

  const [quickFilter, setQuickFilter] = useState(null);

  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Address",
        field: "propertyAddress",
        width: 180,
        minWidth: 150,
        valueFormatter: (params) => {
          return params?.data?.propertyAddress?.fullAddress;
        },
      },
      {
        headerName: "Experience Type",
        field: "experienceType",
        flex: 1,
        minWidth: 150,
        // sort: "asc",
        // checkboxSelection: true,
        // headerCheckboxSelection: true,
      },
      {
        headerName: "Resolution Status",
        field: "resolutionStatus",
        width: 180,
        minWidth: 150,
      },
      {
        headerName: "Purchase Date",
        field: "propertyPurchaseDate",
        width: 130,
        type: "date",
        // valueFormatter: (params) => {
        //   return params.value ? formatDateShort(params.value) : "-";
        // },
      },
      {
        headerName: "Purchase Price",
        field: "propertyPurchasePrice",
        width: 140,
        type: "dollar",
        // valueFormatter: (params) => {
        //   return params.value ? formatDollar(params.value) : "-";
        // },
      },
      {
        headerName: "Renovation Budget",
        field: "propertyRenovationBudget",
        width: 150,
        type: "dollar",
        // valueFormatter: (params) => {
        //   return params.value ? formatDollar(params.value) : "-";
        // },
      },
      {
        headerName: "Sale Date",
        field: "propertySaleDate",
        width: 130,
        type: "date",
        // valueFormatter: (params) => {
        //   return params.value ? formatDateShort(params.value) : "-";
        // },
      },
      {
        headerName: "Sale Price",
        field: "propertySalePrice",
        width: 140,
        type: "dollar",
        // valueFormatter: (params) => {
        //   return params.value ? formatDollar(params.value) : "-";
        // },
      },
      {
        headerName: "Current Market Value",
        field: "currentMarketValue",
        width: 160,
        type: "dollar",
        // valueFormatter: (params) => {
        //   return params.value ? formatDollar(params.value) : "-";
        // },
      },
      {
        headerName: "Entity Ownership %",
        field: "borrowerEntityMembershipPercent",
        width: 150,
        type: "percent",
        // valueFormatter: (params) => {
        //   return params.value ? formatPercent(params.value) : "-";
        // },
      },
    ],
    [],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesDollar(),
      ...columnTypesPercent(),
    };
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      filter: false,
    };
  }, []);

  return (
    <Flx
      column
      sx={{
        p: 0,
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Flx
        wrap
        fw
        jb
        ac
        gap={2}
        sx={{
          p: 2,
          background: grey[100],
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <SearchInput
          fullWidth
          value={quickFilter}
          sx={{ flexBasis: "400px", flexGrow: 1, background: "#ffffff" }}
          onChange={(e) => setQuickFilter(e)}
        />
      </Flx>
      <AgGridTableWrapper simpleTable>
        <AgGridReact
          ref={ref}
          rowData={experiences}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilter}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          onRowClicked={handleRowClick}
        />
      </AgGridTableWrapper>
    </Flx>
  );
};

export default BorrowerExperienceSection;
